
import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/components/ui/use-toast";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Shield, ShieldCheck, ShieldOff, AlertCircle } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AppRole } from "@/utils/routePermissions";

type UserWithRole = {
  id: string;
  email: string;
  role: AppRole | null;
  name?: string;
};

export default function RolesManagement() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [users, setUsers] = useState<UserWithRole[]>([]);
  const [currentUserRole, setCurrentUserRole] = useState<AppRole | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [currentCompanyId, setCurrentCompanyId] = useState<number | null>(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<AppRole>("user");
  const [inviting, setInviting] = useState(false);
  const [showNoRolesWarning, setShowNoRolesWarning] = useState(false);

  // Fetch current user info
  const { data: currentUser } = useQuery({
    queryKey: ['currentUser'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      return user;
    }
  });

  // Get current user's company id
  const { data: userCompany } = useQuery({
    queryKey: ['userCompany', currentUser?.id],
    queryFn: async () => {
      if (!currentUser?.id) return null;
      
      // Using users view instead of profiles
      const { data, error } = await supabase
        .from('users')
        .select('company_id')
        .eq('user_id', currentUser.id)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!currentUser?.id
  });

  // Fetch current user's role
  const { data: userRole, isLoading: isLoadingRole } = useQuery({
    queryKey: ['currentUserRole', currentUser?.id],
    queryFn: async () => {
      if (!currentUser?.id) return null;
      
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', currentUser.id)
        .maybeSingle();
      
      if (error) throw error;
      return data?.role as AppRole | null;
    },
    enabled: !!currentUser?.id
  });

  // Fetch all users with their roles from the same company using the users view
  const { data: usersWithRoles, isLoading: isLoadingUsers, error: usersError } = useQuery({
    queryKey: ['usersWithRoles', userCompany?.company_id],
    queryFn: async () => {
      if (!currentUser || !userCompany?.company_id) return [];
      
      // Get all users from the same company using the users view
      const { data: companyUsers, error: usersError } = await supabase
        .from('users')
        .select('user_id, email, role, name, company_id')
        .eq('company_id', userCompany.company_id)
        .order('name');
      
      if (usersError) throw usersError;
      
      if (!companyUsers || companyUsers.length === 0) {
        return [];
      }
      
      // Map to our UserWithRole type
      const mappedUsers = companyUsers.map(u => {
        return {
          id: u.user_id,
          email: u.email || "Email not available",
          role: u.role as AppRole | null,
          name: u.name
        };
      });
      
      return mappedUsers;
    },
    enabled: !!currentUser?.id && !!userCompany?.company_id
  });

  // Update local state when data is fetched
  useEffect(() => {
    if (currentUser?.id) {
      setCurrentUserId(currentUser.id);
    }
    
    if (userRole) {
      setCurrentUserRole(userRole);
    }
    
    if (userCompany?.company_id) {
      setCurrentCompanyId(userCompany.company_id);
    }
    
    if (usersWithRoles) {
      setUsers(usersWithRoles);
      
      // Check if no users have roles set
      const hasRoles = usersWithRoles.some(user => user.role !== null);
      setShowNoRolesWarning(!hasRoles && usersWithRoles.length > 0);
    }
  }, [currentUser, userRole, usersWithRoles, userCompany]);

  // Define default roles for the first user if needed
  const assignDefaultOwnerRole = async () => {
    if (!currentUserId) return;
    
    try {
      // Insert owner role for the current user
      const { error } = await supabase
        .from('user_roles')
        .insert([{ user_id: currentUserId, role: "owner" }]);

      if (error) throw error;
      
      // Refetch data
      queryClient.invalidateQueries({ queryKey: ['usersWithRoles'] });
      queryClient.invalidateQueries({ queryKey: ['currentUserRole'] });
      
      toast({
        title: "Role assigned",
        description: "You've been assigned the owner role",
      });
      
      setShowNoRolesWarning(false);
    } catch (error: any) {
      console.error("Error assigning role:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to assign role",
        variant: "destructive",
      });
    }
  };

  const handleRoleChange = async (userId: string, newRole: AppRole) => {
    try {
      // Check if current user can change this role
      if (currentUserRole !== 'owner' && newRole === 'owner') {
        toast({
          title: "Permission Denied",
          description: "Only owners can assign owner role",
          variant: "destructive",
        });
        return;
      }

      // If target user is owner and current user is not owner
      const targetUser = users.find(u => u.id === userId);
      if (targetUser?.role === 'owner' && currentUserRole !== 'owner') {
        toast({
          title: "Permission Denied",
          description: "You cannot modify an owner's role",
          variant: "destructive",
        });
        return;
      }

      // Delete existing role
      const { error: deleteError } = await supabase
        .from('user_roles')
        .delete()
        .eq('user_id', userId);

      if (deleteError) throw deleteError;

      // Insert new role
      const { error: insertError } = await supabase
        .from('user_roles')
        .insert({ user_id: userId, role: newRole });

      if (insertError) throw insertError;

      // Update local state
      setUsers(prevUsers =>
        prevUsers.map(u =>
          u.id === userId ? { ...u, role: newRole } : u
        )
      );

      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['usersWithRoles'] });
      
      toast({
        title: "Role Updated",
        description: `User role has been updated to ${newRole}`,
      });
    } catch (error: any) {
      console.error("Error updating role:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to update user role",
        variant: "destructive",
      });
    }
  };

  const handleInvite = async () => {
    if (!inviteEmail) {
      toast({
        title: "Error",
        description: "Please enter an email address",
        variant: "destructive",
      });
      return;
    }

    try {
      setInviting(true);
      
      // This would typically send an invitation email
      // For now, we'll just show a success message
      toast({
        title: "Invitation Sent",
        description: `Invitation sent to ${inviteEmail} with ${inviteRole} role`,
      });
      
      setInviteEmail("");
    } catch (error) {
      console.error("Error inviting user:", error);
      toast({
        title: "Error",
        description: "Failed to send invitation",
        variant: "destructive",
      });
    } finally {
      setInviting(false);
    }
  };

  const getRoleIcon = (role: AppRole | null) => {
    switch (role) {
      case 'owner':
        return <Shield className="h-4 w-4 text-purple-500" />;
      case 'admin':
        return <ShieldCheck className="h-4 w-4 text-blue-500" />;
      case 'manager':
        return <ShieldCheck className="h-4 w-4 text-green-500" />;
      case 'user':
        return <ShieldOff className="h-4 w-4 text-gray-500" />;
      default:
        return <ShieldOff className="h-4 w-4 text-gray-300" />;
    }
  };

  if (isLoadingRole || isLoadingUsers) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (usersError) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Error loading users: {(usersError as Error).message}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      {showNoRolesWarning && (
        <Alert variant="warning" className="bg-amber-50 border-amber-200">
          <AlertCircle className="h-4 w-4 text-amber-500" />
          <AlertDescription className="flex items-center justify-between">
            <span>No user roles have been assigned yet. As the first user, you should be the owner.</span>
            <Button onClick={assignDefaultOwnerRole} variant="outline" className="ml-4">
              Assign Owner Role to Me
            </Button>
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Users and Permissions</CardTitle>
          <CardDescription>
            Manage user roles and permissions for your organization
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="mb-6">
            <h3 className="text-md font-medium mb-2">Invite New User</h3>
            <div className="flex flex-wrap gap-2">
              <Input
                placeholder="Email address"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                className="max-w-sm"
              />
              <Select value={inviteRole} onValueChange={(value: AppRole) => setInviteRole(value)}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  {currentUserRole === 'owner' && (
                    <SelectItem value="owner">Owner</SelectItem>
                  )}
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="user">User</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={handleInvite} disabled={inviting || !inviteEmail}>
                {inviting ? "Sending..." : "Invite User"}
              </Button>
            </div>
          </div>

          <h3 className="text-md font-medium mb-2">User Permissions</h3>
          <div className="border rounded-md">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoadingUsers ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-4">
                      Loading users...
                    </TableCell>
                  </TableRow>
                ) : users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-4">
                      No users found
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>{user.name || 'Unnamed User'}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell className="flex items-center gap-2">
                        {getRoleIcon(user.role)}
                        {user.role || 'No Role'}
                      </TableCell>
                      <TableCell>
                        {/* Don't allow changing own role */}
                        {user.id !== currentUserId && (
                          <Select 
                            value={user.role || undefined} 
                            onValueChange={(value: AppRole) => handleRoleChange(user.id, value)}
                          >
                            <SelectTrigger className="w-[150px]">
                              <SelectValue placeholder="Set Role" />
                            </SelectTrigger>
                            <SelectContent>
                              {/* Only owner can assign owner role */}
                              {currentUserRole === 'owner' && (
                                <SelectItem value="owner">Owner</SelectItem>
                              )}
                              <SelectItem value="admin">Admin</SelectItem>
                              <SelectItem value="manager">Manager</SelectItem>
                              <SelectItem value="user">User</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                        {user.id === currentUserId && (
                          <span className="text-sm text-muted-foreground">Current user</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Role Permissions</CardTitle>
          <CardDescription>
            Role capabilities and access levels
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Role</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Access</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow>
                <TableCell className="font-medium flex items-center gap-2">
                  <Shield className="h-4 w-4 text-purple-500" />
                  Owner
                </TableCell>
                <TableCell>Company owner with full control</TableCell>
                <TableCell>Full access to everything, can manage all roles</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-medium flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-blue-500" />
                  Admin
                </TableCell>
                <TableCell>Administrator with extended privileges</TableCell>
                <TableCell>Full access to everything, can manage roles except owner</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-medium flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-green-500" />
                  Manager
                </TableCell>
                <TableCell>Management team with broad access</TableCell>
                <TableCell>Access to everything except Company settings</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-medium flex items-center gap-2">
                  <ShieldOff className="h-4 w-4 text-gray-500" />
                  User
                </TableCell>
                <TableCell>Basic user with limited access</TableCell>
                <TableCell>Access to Board and Dashboard only</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
